import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Heart, Share2, Edit3, Feather } from 'lucide-react';
import '../styles/Home.css';
import AuthModal from '../components/AuthModal';

// Animasyon için custom hook
const useScrollAnimation = () => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return [ref, isInView];
};


function Home() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authType, setAuthType] = useState('signup');

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const carouselImages = [
    'https://images.unsplash.com/photo-1507842217343-583bb7270b66?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    'https://images.unsplash.com/photo-1532012197267-da84d127e765?auto=format&fit=crop&w=1350&q=80',
    'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?auto=format&fit=crop&w=1350&q=80'
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % carouselImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [carouselImages.length]);

  const handleThumbnailClick = (index) => {
    setCurrentImageIndex(index);
  };

  const openAuthModal = (type) => {
    setAuthType(type);
    setShowAuthModal(true);
  };

  const categories = [
    'Aksiyon', 'Bilim Kurgu', 'Fantastik', 'Genel Kurgu', 'Genç Kurgu',
    'Gerilim', 'Gizem', 'Hayran Kurgu', 'Klasikler', 'Korku',
    'Kısa Hikaye', 'Macera', 'Mizah', 'Paranormal', 'Romantizm',
    'Spiritüel', 'Tarihi Kurgu', 'Texting', 'Vampir', 'Şiir'
  ];
  
  const [socialRef, isSocialInView] = useScrollAnimation();
  const [categoriesRef, isCategoriesInView] = useScrollAnimation();
  const [writeRef, isWriteInView] = useScrollAnimation();

  return (
    <div className="app-container">
      <section className="hero">
        <div className="hero-content">
          <h1>Kitap Tutkunları İçin Sosyal Platform</h1>
          <p>Oku, Paylaş, Keşfet, Yorum Yap - Sınırsız Ücretsiz Okuma Hakkı!</p>
          <button className="cta-button" onClick={() => openAuthModal('signup')}>Hemen Başlayın</button>
        </div>
        <div className="hero-gallery">
          <div className="main-image-container">
            {carouselImages.map((src, index) => (
              <img
                key={src}
                src={src}
                alt="Ana kitap görseli"
                className={`main-image ${index === currentImageIndex ? 'active' : ''}`}
              />
            ))}
          </div>
          <div className="thumbnail-strip">
            {carouselImages.map((src, index) => (
              <div 
                key={`thumb-${src}`}
                className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                onClick={() => handleThumbnailClick(index)}
              >
                <img src={src} alt={`Küçük görsel ${index + 1}`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section ref={socialRef} className={`social-features ${isSocialInView ? 'visible' : ''}`}>
        <h2 className="section-title">Librozi'de Sosyalleşin</h2>
        <div className="social-grid">
          <div className="social-card">
            <div className="social-icon"><MessageSquare size={32} /></div>
            <h3>Kitaplar Hakkında Tartışın</h3>
            <p>Okuduğunuz kitaplar hakkında düşüncelerinizi paylaşın ve diğer okuyucuların yorumlarını görün.</p>
          </div>
          <div className="social-card">
            <div className="social-icon"><Heart size={32} /></div>
            <h3>Beğenin ve Etkileşimde Bulunun</h3>
            <p>Beğendiğiniz kitap yorumlarını destekleyin, etkileşimde bulunun ve tartışmalara katılın.</p>
          </div>
          <div className="social-card">
            <div className="social-icon"><Share2 size={32} /></div>
            <h3>Keşiflerinizi Paylaşın</h3>
            <p>Sevdiğiniz kitapları arkadaşlarınızla paylaşın ve okuma listenizi genişletin.</p>
          </div>
          <div className="social-card">
            <div className="social-icon"><Edit3 size={32} /></div>
            <h3>Kendi Kitabınızı Yazın</h3>
            <p>Yazarlık yeteneğinizi keşfedin ve kendi eserlerinizi toplulukla paylaşın.</p>
          </div>
        </div>
      </section>

      <section ref={categoriesRef} className={`categories ${isCategoriesInView ? 'visible' : ''}`}>
        <h2 className="section-title">20 Farklı Kategori, Yüzlerce Kitap</h2>
        <p className="section-description">Tüm kategorilerde sınırsız ücretsiz okuma hakkı</p>
        <div className="category-tags">
          {categories.map((category, index) => (
            <div key={index} className="category-tag" onClick={() => openAuthModal('signup')}>{category}</div>
          ))}
        </div>
      </section>

      <section ref={writeRef} className={`write-section ${isWriteInView ? 'visible' : ''}`}>
        <div className="write-content">
          <div className="write-icon"><Feather size={48} /></div>
          <h2>Kendi Kitabınızı Yazın ve Paylaşın</h2>
          <p>İçinizdeki yazarı keşfedin. Hikayelerinizi, düşüncelerinizi ve bilgilerinizi kitap formatında paylaşın.</p>
          <button className="write-button" onClick={() => openAuthModal('signup')}>Yazmaya Başlayın</button>
        </div>
      </section>

      <section className="cta-section">
        <div className="cta-content">
          <h2>Okuma Yolculuğunuza Bugün Başlayın</h2>
          <p>Sınırsız ücretsiz okuma hakkı ile kitap dünyasına dalın.</p>
          <button className="cta-button" onClick={() => openAuthModal('signup')}>Ücretsiz Katılın</button>
        </div>
      </section>

      {showAuthModal && (
        <AuthModal
          type={authType}
          onClose={() => setShowAuthModal(false)}
        />
      )}

      <footer>
        <div className="footer-content">
          <div className="footer-column">
            <ul>
              <li><Link to="/agreements#privacy">Gizlilik Sözleşmesi</Link></li>
              <li><Link to="/agreements#data-protection">Kişisel Verilerin Korunumu Kanunu</Link></li>
              <li><Link to="/agreements#terms">Hizmet Koşulları</Link></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 Librozi. Tüm hakları saklıdır.</p>
        </div>
      </footer>
    </div>
  );
}

export default Home;